import { getRecaptchaSiteKey } from "@/constants/appConstants";
import { useLogin } from "@/context/useLoginProps";
import { useRecaptchaV3 } from "@/hooks";
import { Button, Edit, OTPInput } from "@monsterindia/global-ui-components";
import React, { useEffect, useState } from "react";

import Heading from "../../common/heading";
import { useSendOTP, useVerifyOTP } from "../query";
import { LoginData } from "../type";

const ForgotPassword: React.FC = () => {
  const [otp, setOtp] = useState("");
  const {
    loginStep,
    loginData,
    hostname,
    showToast,
    setLoginData,
    setLoginStep,
    translation: { login, errorMessages },
    handleEventTracking,
  } = useLogin();

  const [otpId, setOtpId] = useState<string | undefined>(loginData.otpId);
  const [otpError, setOtpError] = useState<string>("");

  const selectedEmailUuid = loginData.selectedEmailFromPopup?.uuid;

  const { executeRecaptcha } = useRecaptchaV3(
    getRecaptchaSiteKey(hostname),
    true,
  );

  const {
    mutate: verifyUser,
    data: userVerifyData,
    status: verifyUserStatus,
  } = useVerifyOTP();

  const { mutate: sendOTP, data: sendOtpData } = useSendOTP();

  const loginLoading = verifyUserStatus === "pending";

  const isEmail = loginStep === "FORGOT_PASSWORD_EMAIL";

  const handleOTPResend = async () => {
    // Track Resend OTP with GA dataLayer
    if (typeof window !== "undefined" && window.dataLayer) {
      window.dataLayer.push({
        event: "resend_otp",
        eventCategory: "Forgot-Password",
        eventAction: "Resend-OTP",
        eventLabel: "",
      });
    }
    handleEventTracking({
      eventType: "eventPush",
      eventName: "Login_Forgot_Password_OTP_Resend_Clicked",
      eventProps: {
        otp_channel: isEmail ? "email" : "sms",
      },
    });
    setOtpError("");
    setOtp("");
    const { reCaptchaToken, reCaptchaAction } =
      await executeRecaptcha("resend");
    const username = `${isEmail && !selectedEmailUuid ? "" : loginData.countryCode}${loginData.username}`;
    sendOTP(
      {
        username,
        reCaptchaToken,
        reCaptchaAction,
        ...(selectedEmailUuid && { uuid: selectedEmailUuid }),
      },
      {
        onError: (error) => {
          showToast({
            show: true,
            type: "error",
            message: error.message,
          });
        },
      },
    );
  };

  useEffect(() => {
    if (sendOtpData && sendOtpData.id) {
      setLoginData((prevData: LoginData) => {
        const updated = { ...prevData, otpId: sendOtpData.id };
        return updated;
      });
      setOtpId(sendOtpData.id);
    }
  }, [sendOtpData, setLoginData]);

  useEffect(() => {
    setOtpId(loginData.otpId);
  }, [loginData.otpId]);

  useEffect(() => {
    if (userVerifyData?.status === "VALID") {
      setLoginStep("CHANGE_PASSWORD");
      setLoginData((prevData: LoginData) => {
        const updated = { ...prevData, otp };
        return updated;
      });
      handleEventTracking({
        eventType: "eventPush",
        eventName: "Login_Forgot_Password_OTP_Verified",
        eventProps: {
          status: "success",
        },
      });
    } else if (userVerifyData?.status === "INVALID") {
      showToast({
        show: true,
        type: "error",
        message: userVerifyData?.message || errorMessages.somethingWentWrong,
      });

      handleEventTracking({
        eventType: "eventPush",
        eventName: "Login_Forgot_Password_OTP_Verification_Error",
        eventProps: {
          error_type: userVerifyData?.message || "wrong_otp",
        },
      });
    }
  }, [userVerifyData]);

  const handleOTPSubmit = () => {
    if (otp.length === 6) {
      setOtpError("");
      verifyUser(
        {
          otp,
          otpId: otpId ?? "",
          payload: {
            countryCode: isEmail ? undefined : loginData.countryCode,
            mobileNumber: isEmail ? undefined : loginData.username,
            email: isEmail ? loginData.username : undefined,
          },
        },
        {
          onError: (error) => {
            setOtpError(
              error instanceof Error
                ? error.message
                : "Login failed. Please try again.",
            );
            // showToast({
            //   show: true,
            //   type: "error",
            //   message: error.message,
            // });
          },
        },
      );
      handleEventTracking({
        eventType: "eventPush",
        eventName: "Login_Forgot_Password_OTP_Entered",
        eventProps: {
          otp_entered_value: otp,
        },
      });
    }
  };

  return (
    <>
      <Heading
        title={login.forgotPassword}
        text={isEmail ? login.sentOtpOnEmail : login.sentOtpOnNumber}
      />
      <div className="text-content-tertiary flex flex-wrap items-center gap-1 text-sm">
        <span className="text-content-tertiary text-sm font-semibold">
          {selectedEmailUuid
            ? loginData.selectedEmailFromPopup?.maskedEmail
            : `${isEmail ? "" : loginData?.countryCode} ${loginData?.username}`}
        </span>
        {login.toSetNewPassword}
        <Edit
          width={16}
          height={16}
          color="#6E00BE"
          onClick={() => setLoginStep("LOGIN")}
          className="cursor-pointer"
        />
      </div>

      <div className="my-[52px]">
        <OTPInput
          otpValue={otp}
          resendTime={30}
          errorMessage={otpError}
          otpUpdateHandler={(newOTP: string) => {
            setOtp(newOTP);
            setOtpError("");
          }}
          resendClickHandler={handleOTPResend}
        />
      </div>

      <Button
        size="lg"
        id="loginSubmit"
        modifier="primary"
        aria-label="submit"
        className="w-full rounded-full"
        disabled={otp.length === 6 ? false : true}
        label={login.submitOtp}
        isLoading={loginLoading}
        onClick={handleOTPSubmit}
      />
    </>
  );
};

export default ForgotPassword;
